import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../Auth/AuthContext';
import authFetch from '../../helpers/authFetch';
import { VIEW_MODE_STORAGE_MAP } from '../../helpers/viewModeConstants';

const PreferencesContext = createContext(null);

const LOCALSTORAGE_THEME_KEY = 'mtg_theme';

const DEFAULT_PREFERENCES = {
  theme: 'dark',
  default_deck_visibility: 'PRIVATE',
  default_deck_format: null,
  card_display_size: 'normal',
  card_display_mode: 'table',
};

const getLocalTheme = () => {
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_THEME_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage not available
  }
  return 'dark';
};

const setLocalTheme = (theme) => {
  try {
    localStorage.setItem(LOCALSTORAGE_THEME_KEY, theme);
  } catch {
    // localStorage not available
  }
};

export const PreferencesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [preferences, setPreferences] = useState(() => ({
    ...DEFAULT_PREFERENCES,
    theme: getLocalTheme(),
  }));
  const [loading, setLoading] = useState(false);
  const prevAuthRef = useRef(isAuthenticated);

  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/preferences');
      if (res.ok) {
        const serverPrefs = await res.json();
        // Convert storage format ('list'/'grid') to UI format ('table'/'visual')
        if (serverPrefs.card_display_mode) {
          serverPrefs.card_display_mode = VIEW_MODE_STORAGE_MAP[serverPrefs.card_display_mode];
        }
        setPreferences((prev) => ({ ...prev, ...serverPrefs }));
        if (serverPrefs.theme) setLocalTheme(serverPrefs.theme);
      }
    } catch {
      // Silently fall back to current state
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount: fetch if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When auth state changes after mount
  useEffect(() => {
    const wasAuthenticated = prevAuthRef.current;
    prevAuthRef.current = isAuthenticated;

    if (!wasAuthenticated && isAuthenticated) {
      // User just logged in — fetch server preferences (server wins)
      fetchPreferences();
    } else if (wasAuthenticated && !isAuthenticated) {
      // User just logged out — revert to localStorage-only theme
      setPreferences({
        ...DEFAULT_PREFERENCES,
        theme: getLocalTheme(),
      });
    }
  }, [isAuthenticated, fetchPreferences]);

  const preferencesRef = useRef(preferences);
  preferencesRef.current = preferences;

  const updatePreference = useCallback(async (key, value) => {
    const snapshot = preferencesRef.current;
    setPreferences((prev) => ({ ...prev, [key]: value }));

    if (key === 'theme') {
      setLocalTheme(value);
    }

    if (isAuthenticated) {
      try {
        // Convert UI format ('table'/'visual') to storage format ('list'/'grid') for API
        const bodyValue = key === 'card_display_mode' ? VIEW_MODE_STORAGE_MAP[value] : value;
        const res = await authFetch('/preferences', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ [key]: bodyValue }),
        });
        if (res.ok) {
          const serverPrefs = await res.json();
          // Convert storage format to UI format on response
          if (serverPrefs.card_display_mode) {
            serverPrefs.card_display_mode = VIEW_MODE_STORAGE_MAP[serverPrefs.card_display_mode];
          }
          setPreferences((prev) => ({ ...prev, ...serverPrefs }));
        } else {
          setPreferences(snapshot);
          if (key === 'theme') setLocalTheme(snapshot.theme);
        }
      } catch {
        setPreferences(snapshot);
        if (key === 'theme') setLocalTheme(snapshot.theme);
      }
    }
  }, [isAuthenticated]);

  return (
    <PreferencesContext.Provider value={{ preferences, updatePreference, loading }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

export default PreferencesContext;
